import React, { useState, useEffect } from 'react';
import './VacationApprovalDashboard.css';
import { 
  loadVacationRequests, 
  approveVacationRequest, 
  rejectVacationRequest 
} from './api/vacationRequestsApi';

function formatDateSK(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function getStatusLabel(status) {
  const labels = {
    'pending': '⏳ Čaká na schválenie',
    'approved': '✓ Schválené',
    'rejected': '✗ Odmietnuté'
  };
  return labels[status] || status;
}

function getStatusColor(status) {
  const colors = {
    'pending': '#FFA500',
    'approved': '#4CAF50',
    'rejected': '#f44336'
  };
  return colors[status] || '#999';
}

export default function VacationApprovalDashboard({ pin, supervisorData }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSupervisor, setIsSupervisor] = useState(false);
  
  // For rejection
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionStatus, setRejectionStatus] = useState('idle');

  // Filter
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    setError('');
    const result = await loadVacationRequests(pin);

    if (result.ok) {
      setRequests(result.requests || []);
      setIsSupervisor(result.isSupervisor);
    } else {
      setError(result.error || 'Chyba pri načítaní žiadostí');
      setRequests([]);
    }
    setLoading(false);
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Naozaj chcete schváliť túto dovolenku?')) return;

    const result = await approveVacationRequest(pin, requestId);

    if (result.ok) {
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId ? { ...req, status: 'approved' } : req
        )
      );
    } else {
      alert('Chyba pri schválení: ' + result.error);
    }
  };

  const handleRejectStart = (requestId) => {
    setRejectingRequestId(requestId);
    setRejectionReason('');
  };

  const handleRejectCancel = () => {
    setRejectingRequestId(null);
    setRejectionReason('');
    setRejectionStatus('idle');
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      alert('Prosím zadajte dôvod odmietnutia');
      return;
    }

    setRejectionStatus('loading');
    const result = await rejectVacationRequest(pin, rejectingRequestId, rejectionReason);

    if (result.ok) {
      setRequests(prev => 
        prev.map(req => 
          req.id === rejectingRequestId 
            ? { ...req, status: 'rejected', rejection_reason: rejectionReason } 
            : req
        )
      );
      setRejectingRequestId(null);
      setRejectionReason('');
      setRejectionStatus('idle');
    } else {
      alert('Chyba pri odmietnutí: ' + result.error);
      setRejectionStatus('idle');
    }
  };

  const filteredRequests = requests.filter(req => 
    filterStatus === 'all' ? true : req.status === filterStatus
  );

  if (!isSupervisor) {
    return (
      <div className="vacation-approval-container">
        <div className="no-access">
          <h2>Prístup zamietnutý</h2>
          <p>Nemáte oprávnenie na schvaľovanie dovoleniek.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vacation-approval-container">
      <div className="approval-header">
        <h2>Schvaľovanie dovoleniek</h2>
        {supervisorData?.meno && <p className="supervisor-name">Prihlásený ako: {supervisorData.meno}</p>}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="approval-controls">
        <button 
          className="refresh-button"
          onClick={loadRequests}
          disabled={loading}
        >
          🔄 Obnoviť
        </button>

        <div className="filter-group">
          <label>Filtrovať:</label>
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Všetky ({requests.length})</option>
            <option value="pending">Čaká na schválenie ({requests.filter(r => r.status === 'pending').length})</option>
            <option value="approved">Schválené ({requests.filter(r => r.status === 'approved').length})</option>
            <option value="rejected">Odmietnuté ({requests.filter(r => r.status === 'rejected').length})</option>
          </select>
        </div>
      </div>

      {loading && <p className="loading">Načítavam žiadosti...</p>}

      {!loading && filteredRequests.length === 0 && (
        <p className="no-requests">
          {filterStatus === 'all' ? 'Zatiaľ žiadne žiadosti' : 'Žiadne žiadosti v tomto filtri'}
        </p>
      )}

      {!loading && filteredRequests.length > 0 && (
        <div className="requests-grid">
          {filteredRequests.map(req => (
            <div key={req.id} className="request-card">
              {/* Header */}
              <div className="request-card-header">
                <div>
                  <div className="employee-info">
                    {req.employee_data?.meno && (
                      <span className="employee-name">
                        {req.employee_data.meno}
                      </span>
                    )}
                  </div>
                  <div className="request-dates">
                    {formatDateSK(req.date_from)} — {formatDateSK(req.date_to)}
                  </div>
                </div>
                <span 
                  className="request-status-badge" 
                  style={{ backgroundColor: getStatusColor(req.status) }}
                >
                  {getStatusLabel(req.status)}
                </span>
              </div>

              {/* Details */}
              <div className="request-card-details">
                <p><strong>Typ:</strong> {req.request_type}</p>
                <p><strong>Počet dní:</strong> <span className="days-count">{req.day_count} pracovných dní</span></p>
                {req.notes && <p><strong>Poznámka:</strong> {req.notes}</p>}
                {req.rejection_reason && (
                  <p className="rejection-info"><strong>Dôvod odmietnutia:</strong> {req.rejection_reason}</p>
                )}
                <p className="request-date">Podané: {new Date(req.created_at).toLocaleDateString('sk-SK')}</p>
              </div>

              {/* Actions */}
              {req.status === 'pending' && (
                <div className="request-actions">
                  <button 
                    className="btn-approve"
                    onClick={() => handleApprove(req.id)}
                  >
                    ✓ Schváliť
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={() => handleRejectStart(req.id)}
                  >
                    ✗ Odmietnuť
                  </button>
                </div>
              )}

              {/* Rejection Form */}
              {rejectingRequestId === req.id && (
                <div className="rejection-form">
                  <label>Dôvod odmietnutia:</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Napríklad: Nedostatočný počet zamestnancov v tú dobu..."
                    maxLength="500"
                    rows="3"
                  />
                  <div className="rejection-actions">
                    <button 
                      className="btn-confirm"
                      onClick={handleRejectConfirm}
                      disabled={rejectionStatus === 'loading'}
                    >
                      {rejectionStatus === 'loading' ? 'Posielam...' : 'Potvrdiť odmietnutie'}
                    </button>
                    <button 
                      className="btn-cancel"
                      onClick={handleRejectCancel}
                      disabled={rejectionStatus === 'loading'}
                    >
                      Zrušiť
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
